import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../../api/ApiContext";
import type { CampaignDetail, CampaignPeriodReport, CampaignReport, CampaignShadowReport } from "../../api/types";
import { Banner, Card, EmptyState, Money, MoneyStat, QueryView, StatusPill, Table } from "../../components/ui";
import { formatLocal, viewerTimeZone } from "../../lib/datetime";

// UX-DESIGN.md §2.5. Three reads, three panels:
//   GET /v1/campaigns/{id}/report          live ledger totals (reporting.go)
//   GET /v1/campaigns/{id}/report/periods  the same buckets by local day (G-29)
//   GET /v1/campaigns/{id}/report/shadow   CPE evaluation-mode activity (G-29)
// The shadow figures come from a separate endpoint and render in their own
// panel with their own totals: nothing on this page ever adds a shadow
// figure to a real one.

export function CampaignReports({ c }: { c: CampaignDetail }) {
  return (
    <>
      <ReportCard campaignId={c.campaign_id} />
      <PeriodsCard campaignId={c.campaign_id} timezone={c.budget.budget_timezone ?? viewerTimeZone()} />
      <ShadowCard campaignId={c.campaign_id} />
    </>
  );
}

function asOfLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function ReportCard({ campaignId }: { campaignId: string }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["campaign-report", campaignId], queryFn: () => ep.advertiser.getCampaignReport(campaignId), refetchInterval: 60_000 });
  return (
    <Card title="Spend" subtitle="Live figures from the ledger — not final. Issued statements are the final record.">
      <QueryView query={q} loadingLabel="Loading spend" lines={4}>
        {(r: CampaignReport) => (
          <div className="stack">
            <div className="row">
              <StatusPill tone="info" icon="clock">
                Live — not yet final
              </StatusPill>
              <span className="small muted">as of {asOfLabel(r.as_of)} — figures may still change</span>
              <abbr
                className="small"
                tabIndex={0}
                title="Captured spend stays provisional for the 24-hour dispute and classification window before it's recognized as final."
              >
                Why not final?
              </abbr>
            </div>
            {r.by_currency.length === 0 ? (
              <p className="muted">No spend yet — this campaign hasn't reserved or captured anything.</p>
            ) : (
              r.by_currency.map((row) => (
                <div key={row.currency} className="grid-auto">
                  <MoneyStat label="Spend, finalized" value={row.net_spend_finalized} currency={row.currency} note="Past the dispute window" />
                  <MoneyStat
                    label="Spend incl. provisional"
                    value={row.net_spend_including_provisional}
                    currency={row.currency}
                    note="Includes captured spend still inside the 24h window"
                    alwaysFlag
                  />
                  <MoneyStat label="Reserved, in flight" value={row.in_flight} currency={row.currency} note="Earmarked, not yet captured" alwaysFlag />
                  <MoneyStat label="Released back" value={row.released} currency={row.currency} note="Earmarked then returned to your wallet" />
                </div>
              ))
            )}
          </div>
        )}
      </QueryView>
    </Card>
  );
}

function PeriodsCard({ campaignId, timezone }: { campaignId: string; timezone: string }) {
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["campaign-periods", campaignId, timezone],
    queryFn: () => ep.advertiser.getCampaignPeriodReport(campaignId, timezone),
  });
  return (
    <Card title="By day" subtitle={`Real (billed) activity for the last 30 days, by day in ${timezone}. Live figures — not final.`}>
      <QueryView query={q} loadingLabel="Loading daily figures" lines={3}>
        {(r: CampaignPeriodReport) =>
          r.periods.length === 0 ? (
            <EmptyState icon="chart" title="No reservations in this period" />
          ) : (
            <Table
              caption={`Daily activity, ${r.timezone}, as of ${asOfLabel(r.as_of)}`}
              rows={r.periods}
              rowKey={(p) => `${p.period_start}-${p.currency}`}
              columns={[
                { key: "day", header: "Day starting", cell: (p) => formatLocal(p.period_start) },
                { key: "res", header: "Reservations", cell: (p) => p.reservations, numeric: true },
                { key: "flight", header: "In flight", cell: (p) => <Money value={p.in_flight} currency={p.currency} alwaysFlag />, numeric: true },
                { key: "pend", header: "Captured, pending", cell: (p) => <Money value={p.spend_captured_pending} currency={p.currency} alwaysFlag />, numeric: true },
                { key: "rec", header: "Captured, recognized", cell: (p) => <Money value={p.spend_captured_recognized} currency={p.currency} />, numeric: true },
                { key: "rel", header: "Released", cell: (p) => <Money value={p.released} currency={p.currency} />, numeric: true },
                { key: "rev", header: "Reversed", cell: (p) => <Money value={p.reversed} currency={p.currency} />, numeric: true },
              ]}
            />
          )
        }
      </QueryView>
    </Card>
  );
}

function ShadowCard({ campaignId }: { campaignId: string }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["campaign-shadow", campaignId], queryFn: () => ep.advertiser.getCampaignShadowReport(campaignId) });
  return (
    <Card
      title="CPE evaluation mode"
      subtitle="A separate panel with its own totals. Nothing here is included in the spend figures above, your wallet or your statements."
    >
      <QueryView query={q} loadingLabel="Loading evaluation-mode activity" lines={3}>
        {(r: CampaignShadowReport) => (
          <div className="stack">
            {r.policy_gate_open ? (
              <Banner tone="info" icon="flask" title="CPE billing is active">
                Engagement activity recorded while CPE billing was in evaluation mode is evaluation-only and will not be billed, even
                retroactively. When billing switched over isn't recorded yet.
              </Banner>
            ) : (
              <Banner tone="info" icon="flask" title="Not billed and not final">
                These engagement numbers are <strong>not billed and not final</strong> — CPE billing is in evaluation mode. No charges result
                from this activity yet.
              </Banner>
            )}
            {r.by_currency.length === 0 ? (
              <p className="muted">No evaluation-mode activity in the last 30 days.</p>
            ) : (
              <Table
                caption="Evaluation-mode activity, last 30 days"
                rows={r.by_currency}
                rowKey={(row) => row.currency}
                columns={[
                  { key: "dec", header: "Decisions", cell: (row) => row.decisions, numeric: true },
                  { key: "eng", header: "Engagements", cell: (row) => row.engagements, numeric: true },
                  { key: "pend", header: "Still classifying", cell: (row) => row.engagements_pending, numeric: true },
                  {
                    key: "proj",
                    header: "Projected spend (not billed)",
                    cell: (row) => <Money value={row.projected_spend} currency={row.currency} alwaysFlag />,
                    numeric: true,
                  },
                ]}
              />
            )}
            <p className="xs muted">
              As of {asOfLabel(r.as_of)}.
              {r.classification_window_hours ? ` Engagements are classified for up to ${r.classification_window_hours} hours.` : ""}
            </p>
          </div>
        )}
      </QueryView>
    </Card>
  );
}
